import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Customer } from "@shared/operations-schema";
import { useCurrentBusiness } from "./use-current-business";

export type { Customer } from "@shared/operations-schema";

export function useCustomers() {
  const { toast } = useToast();
  const { currentBusiness } = useCurrentBusiness();
  const businessId = currentBusiness?.id;

  const {
    data: customers = [],
    isLoading,
    error,
    isError,
    refetch,
  } = useQuery<Customer[], Error>({
    queryKey: ["/api/sales/customers", businessId],
    queryFn: async () => {
      if (!businessId) return [];
      const res = await fetch(`/api/sales/customers`, { credentials: "include" });
      if (!res.ok) {
        if (res.status === 400 || res.status === 401) return []; // No session/business — return empty
        throw new Error("Failed to fetch customers");
      }
      return res.json() as Promise<Customer[]>;
    },
    enabled: !!businessId,
  });

  const createCustomerMutation = useMutation({
    mutationFn: async (data: Partial<Customer>) => {
      return apiRequest("POST", "/api/sales/customers", {
        ...data,
      });
    },
    onSuccess: () => {
      toast({
        title: "Customer created",
        description: "The customer has been successfully created.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sales/customers", businessId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating customer",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Customer> }) => {
      return apiRequest("PATCH", `/api/sales/customers/${id}`, {
        ...data,
      });
    },
    onSuccess: () => {
      toast({
        title: "Customer updated",
        description: "The customer has been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sales/customers", businessId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating customer",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/sales/customers/${id}`);
    },
    onSuccess: () => {
      toast({
        title: "Customer deleted",
        description: "The customer has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sales/customers", businessId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting customer",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    customers,
    isLoading,
    error,
    isError,
    refetch,
    createCustomerMutation,
    updateCustomerMutation,
    deleteCustomerMutation,
  };
}
